import { gql, useQuery } from '@apollo/client'
import { Box, Button, SkeletonCircle, SkeletonText } from '@chakra-ui/react'
import { useAdaptedReviewable } from 'lodestar-app-element/src/hooks/review'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import hasura from '../../hasura'
import { commonMessages } from '../../helpers/translation'
import { ReviewProps } from '../../types/review'
import { StyledDivider } from './ReviewCollectionBlock'
import ReviewItem from './ReviewItem'

const ReviewPublicItemCollection: React.FC<{
  targetId: string
  path: string
  appId: string
}> = ({ path, appId, targetId }) => {
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(false)

  const { loadingReviews, publicReviews, loadMoreReviews } = useReviewPublicCollection(path, appId)
  const { data: reviewable, loading: reviewableLoading } = useAdaptedReviewable(path, appId)

  if (loadingReviews && reviewableLoading) {
    return (
      <Box padding="6" boxShadow="lg" bg="white">
        <SkeletonCircle size="36" />
        <SkeletonText mt="1" noOfLines={4} spacing="4" />
      </Box>
    )
  }

  return !reviewable?.is_item_viewable ? (
    <></>
  ) : (
    <>
      <div>
        {publicReviews.map(v => (
          <div key={v.id} className="review-item">
            <ReviewItem
              likedCount={v.reactionCount}
              id={v.id}
              memberId={v.memberId}
              score={v.score}
              title={v.title}
              content={v.content}
              createdAt={v.createdAt}
              updatedAt={v.updatedAt}
              reviewReplies={v.reviewReplies}
              targetId={targetId}
            />
            <StyledDivider className="review-divider" />
          </div>
        ))}
      </div>
      {loadMoreReviews && (
        <div className="text-center mt-4 load-more-reviews">
          <Button
            isLoading={loading}
            variant="outline"
            onClick={() => {
              setLoading(true)
              loadMoreReviews().finally(() => setLoading(false))
            }}
          >
            {formatMessage(commonMessages.button.loadMore)}
          </Button>
        </div>
      )}
    </>
  )
}

const useReviewPublicCollection = (path: string, appId: string) => {
  const condition: hasura.GET_REVIEW_PUBLICVariables['condition'] = {
    path: { _eq: path },
    app_id: { _eq: appId },
  }
  const { loading, error, data, refetch, fetchMore } = useQuery<
    hasura.GET_REVIEW_PUBLIC,
    hasura.GET_REVIEW_PUBLICVariables
  >(
    gql`
      query GET_REVIEW_PUBLIC($condition: review_public_bool_exp, $limit: Int!) {
        review_public_aggregate(where: $condition) {
          aggregate {
            count
          }
        }
        review_public(where: $condition, order_by: { created_at: desc }, limit: $limit) {
          id
          member_id
          score
          title
          content
          created_at
          updated_at
          review_reactions_aggregate {
            aggregate {
              count
            }
          }
          review_replies(order_by: { created_at: desc }) {
            id
            member_id
            content
            created_at
            updated_at
          }
        }
      }
    `,
    {
      variables: {
        condition,
        limit: 5,
      },
    },
  )
  const publicReviews: (ReviewProps & {
    reactionCount: number
  })[] =
    data?.review_public.map(v => ({
      id: v.id,
      memberId: v.member_id || null,
      score: v.score,
      title: v.title || null,
      content: v.content || null,
      createdAt: new Date(v.created_at),
      updatedAt: new Date(v.updated_at),
      reactionCount: v.review_reactions_aggregate.aggregate?.count || 0,
      reviewReplies: v?.review_replies.map(v => ({
        id: v.id,
        reviewReplyMemberId: v.member_id,
        memberRole: 'general-member',
        content: v.content || null,
        createdAt: new Date(v.created_at),
        updatedAt: new Date(v.updated_at),
      })),
    })) || []

  const loadMoreReviews = () =>
    fetchMore({
      variables: {
        condition: {
          ...condition,
          created_at: { _lt: data?.review_public.slice(-1)[0]?.created_at },
        },
        limit: 5,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev
        }
        return Object.assign({}, prev, {
          review_public: [...prev.review_public, ...fetchMoreResult.review_public],
        })
      },
    })

  return {
    loadingReviews: loading,
    errorReviews: error,
    publicReviews,
    onRefetch: refetch,
    loadMoreReviews: (data?.review_public_aggregate.aggregate?.count || 0) > 5 ? loadMoreReviews : undefined,
  }
}

export default ReviewPublicItemCollection
